/**
 * Confirmation for irreversible actions.
 *
 * Any form carrying `data-confirm` gets an interstitial before it submits.
 * Native `confirm()` is deliberately avoided — it blocks the page and, in this
 * automation context, wedges the browser. This renders the design system's
 * `.dialog` instead, which also lets the message keep the site's typography.
 *
 * Without JavaScript the form submits directly: the server-side checks are what
 * actually protect the action, so losing the prompt loses a courtesy, not a
 * control.
 */
export function initConfirmDialogs(root = document) {
  root.querySelectorAll("form[data-confirm]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      if (form.dataset.confirmed === "yes") return;
      const dialog = buildDialog(form.dataset.confirm);
      // No template on the page means no prompt, and the form submits as it
      // would with JavaScript off. The server-side checks are what protect the
      // action; a dialog assembled here instead would be this module writing
      // copy again, which is the defect it just stopped having.
      if (!dialog) return;
      event.preventDefault();
      openDialog(dialog, () => {
        form.dataset.confirmed = "yes";
        form.submit();
      });
    });
  });
}

/**
 * The dialog as the server rendered it, with this form's message in it.
 *
 * Every string in it is the page's own language, because every string in it
 * came from the page. This module used to hold the title and the two button
 * labels in Czech and Ukrainian and pick between them on `<html lang>`, which
 * is both a language list in the wrong file and, on a third language, Czech.
 */
function buildDialog(message) {
  const template = document.querySelector("template[data-confirm-dialog]");
  if (!template) return null;

  const backdrop = template.content.firstElementChild.cloneNode(true);
  // Text, never markup: the message comes from a template and may carry a job
  // title somebody typed.
  backdrop.querySelector(".dialog-body").textContent = message || "";
  return backdrop;
}

function openDialog(backdrop, onConfirm) {
  const close = () => backdrop.remove();
  backdrop.querySelector("[data-cancel]").addEventListener("click", close);
  backdrop.querySelector("[data-ok]").addEventListener("click", () => {
    close();
    onConfirm();
  });
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop) close();
  });
  document.addEventListener("keydown", function onKey(event) {
    if (event.key === "Escape") {
      close();
      document.removeEventListener("keydown", onKey);
    }
  });

  document.body.appendChild(backdrop);
  backdrop.querySelector("[data-ok]").focus();
}
